import {
  DailyAudio,
  DailyVideo,
  useLocalSessionId,
  useParticipantIds,
  useVideoTrack,
} from '@daily-co/daily-react'
import { CircleAlert, LoaderCircle, LogOut, RefreshCcw, Video, VideoOff } from 'lucide-react'
import { useEffect, type ReactNode } from 'react'
import { Button } from '~/components/ui/button'

export type VisioPhase = 'starting' | 'connecting' | 'live' | 'error' | 'timeout'

const VideoCall = ({
  phase,
  errorMessage,
  leaveCall,
  onRemoteVideoPlayable,
  onRetry,
}: {
  phase: VisioPhase
  errorMessage: string | null
  leaveCall: () => void
  onRemoteVideoPlayable: () => void
  onRetry: () => void
}) => {
  const remoteParticipantIds = useParticipantIds({ filter: 'remote' })
  const remoteParticipantId = remoteParticipantIds[0] ?? ''
  const remoteVideo = useVideoTrack(remoteParticipantId)
  const remoteVideoPlayable = remoteParticipantId !== '' && remoteVideo.state === 'playable'

  const localSessionId = useLocalSessionId()
  const localVideo = useVideoTrack(localSessionId)
  const localVideoPlayable = localSessionId !== '' && localVideo.state === 'playable'

  useEffect(() => {
    if (remoteVideoPlayable) onRemoteVideoPlayable()
  }, [onRemoteVideoPlayable, remoteVideoPlayable])

  const waiting = phase === 'starting' || phase === 'connecting'
  const failed = phase === 'error' || phase === 'timeout'
  const live = phase === 'live'
  const kioskJoined = remoteParticipantId !== ''

  let phaseLabel = 'Interrompue'
  if (live) phaseLabel = kioskJoined ? 'En direct' : 'En attente de la caisse…'
  else if (waiting) phaseLabel = 'Connexion…'

  let statusHint: string | null = null
  if (live && !kioskJoined) statusHint = 'La caisse ne s’est pas encore connectée à l’appel.'
  else if (live && !remoteVideoPlayable) statusHint = 'Caisse connectée, sans retour vidéo.'

  let videoContent: ReactNode = null
  if (remoteVideoPlayable) {
    videoContent = (
      <DailyVideo
        sessionId={remoteParticipantId}
        type="video"
        fit="contain"
        className="h-full w-full"
      />
    )
  } else if (live && localVideoPlayable) {
    videoContent = (
      <DailyVideo
        sessionId={localSessionId}
        type="video"
        automirror
        fit="contain"
        className="h-full w-full"
      />
    )
  } else if (live) {
    videoContent = (
      <div className="flex flex-col items-center gap-3 px-6 text-center">
        <VideoOff className="size-8 text-white/65" />
        <p className="text-sm font-semibold">Aucune vidéo disponible</p>
        <p className="text-xs text-white/65">L’audio reste actif pendant l’appel.</p>
      </div>
    )
  } else if (waiting) {
    videoContent = (
      <div className="flex flex-col items-center gap-3 px-6 text-center">
        <LoaderCircle className="size-8 animate-spin" />
        <p className="text-sm font-semibold">
          {phase === 'starting' ? "Démarrage de l'intervention…" : 'Connexion à l’appel…'}
        </p>
        <p className="text-xs text-white/65">Arrêt automatique après 12 secondes.</p>
      </div>
    )
  } else {
    videoContent = (
      <div className="flex flex-col items-center gap-3 px-6 text-center">
        <CircleAlert className="size-8 text-amber-300" />
        <p className="text-sm font-semibold">{errorMessage ?? 'Le flux vidéo est indisponible.'}</p>
      </div>
    )
  }

  return (
    <section
      aria-live="polite"
      className="fixed bottom-5 right-5 z-[1000] w-[min(92vw,32rem)] overflow-hidden rounded-3xl border border-[#DEDACB] bg-white shadow-2xl"
    >
      <div className="flex items-center justify-between gap-3 border-b border-[#DEDACB] px-5 py-3">
        <div className="flex min-w-0 items-center gap-2">
          <Video className="size-4 shrink-0 text-primary" />
          <p className="truncate text-sm font-bold text-[#072C1A]">Visio caisse</p>
        </div>
        <span className="text-xs font-semibold text-muted-foreground">{phaseLabel}</span>
      </div>

      <div className="relative flex aspect-video items-center justify-center bg-[#17271F] text-white">
        {videoContent}
        {remoteVideoPlayable && localVideoPlayable ? (
          <div className="absolute bottom-3 left-3 w-[28%] overflow-hidden rounded-xl border border-white/30 bg-black/60 shadow-lg">
            <DailyVideo
              sessionId={localSessionId}
              type="video"
              automirror
              fit="cover"
              className="aspect-video w-full"
            />
          </div>
        ) : null}
        {statusHint ? (
          <p className="absolute left-3 top-3 rounded-full bg-black/55 px-3 py-1 text-xs font-medium">
            {statusHint}
          </p>
        ) : null}
        {live && !remoteVideoPlayable && localVideoPlayable ? (
          <p className="absolute bottom-3 right-3 rounded-full bg-black/55 px-3 py-1 text-xs font-medium">
            Retour de votre caméra
          </p>
        ) : null}
      </div>

      <DailyAudio />
      <div className="flex flex-wrap justify-end gap-2 p-4">
        {failed ? (
          <Button variant="outline" onClick={onRetry}>
            <RefreshCcw className="size-4" /> Réessayer
          </Button>
        ) : null}
        <Button variant={live ? 'destructive' : 'outline'} onClick={leaveCall}>
          <LogOut className="size-4" /> {live ? 'Quitter' : 'Annuler'}
        </Button>
      </div>
    </section>
  )
}

export default VideoCall
